import { Switch } from "@headlessui/react";
import { useState } from "react";
import { toast } from "react-toastify";
import { queryClient } from "../../../main";

interface DemoToggleProps {
  isDemo: boolean;
  disabled?: boolean;
  disabledReason?: string;
  label?: string;
  onPatch: (next: boolean) => Promise<unknown>;
  invalidateKeys?: (string | undefined)[][];
  size?: "sm" | "md";
}

const DemoToggle = ({
  isDemo,
  disabled,
  disabledReason,
  label = "Demo",
  onPatch,
  invalidateKeys,
  size = "sm",
}: DemoToggleProps) => {
  const [enabled, setEnabled] = useState(isDemo);
  const [pending, setPending] = useState(false);

  const handleChange = async (value: boolean) => {
    if (disabled || pending) return;
    const previous = enabled;
    setEnabled(value);
    setPending(true);
    try {
      await onPatch(value);
      invalidateKeys?.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      );
      toast.success(`${label} ${value ? "enabled" : "disabled"}`);
    } catch (err) {
      setEnabled(previous);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.message || `Failed to update ${label}`;
      toast.error(msg);
    } finally {
      setPending(false);
    }
  };

  const dims =
    size === "md"
      ? { track: "h-[30px] w-[58px]", knob: "h-[26px] w-[26px]", shift: "translate-x-7" }
      : { track: "h-[22px] w-[42px]", knob: "h-[18px] w-[18px]", shift: "translate-x-5" };

  return (
    <div
      title={disabled ? disabledReason : undefined}
      className={`flex items-center gap-2 ${disabled ? "opacity-50" : ""}`}
    >
      <span className="text-xs font-medium text-burntBlack">{label}</span>
      <Switch
        checked={enabled}
        onChange={handleChange}
        disabled={disabled || pending}
        className={`${enabled ? "bg-secondary" : "bg-gray-300"} relative inline-flex ${dims.track} shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:cursor-not-allowed`}
      >
        <span
          aria-hidden="true"
          className={`${enabled ? dims.shift : "translate-x-0"} pointer-events-none inline-block ${dims.knob} transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </Switch>
    </div>
  );
};

export default DemoToggle;
